import { CircularProgress } from '@mui/material';
import { RiCloseCircleFill, RiDownloadLine, RiErrorWarningLine, RiFileTextLine } from '@remixicon/react';
import { IMAGE_FILE_EXTENSIONS } from '@shared/constants/FileExtensions';
import { useEffect, useState } from 'react';
import { AgButton, AgText } from 'src/components/ui';
import formatFileSize from 'src/utils/formatFileSize';
import type { AttachedFile } from '../store/aiChat.store';

type AiChatFileAttachmentProps = {
  attachedFile?: AttachedFile;
  document?: { id: number; url: string; title: string };
  onRemove?: () => void;
  mode?: 'edit' | 'view';
};

export default function AiChatFileAttachment({
  attachedFile,
  document,
  onRemove,
  mode = 'edit',
}: AiChatFileAttachmentProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Generate thumbnail for image files
  useEffect(() => {
    if (!attachedFile?.file) return;

    const fileExtension = attachedFile.file.name.split('.').pop()?.toLowerCase();
    const isImage = fileExtension && (IMAGE_FILE_EXTENSIONS as readonly string[]).includes(fileExtension);

    if (isImage) {
      const url = URL.createObjectURL(attachedFile.file);
      setThumbnailUrl(url);

      // Cleanup URL when component unmounts
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [attachedFile?.file]);

  const displayName = document?.title || attachedFile?.file?.name || attachedFile?.filename || 'Unknown file';
  const fileExtension = displayName.substring(displayName.lastIndexOf('.') + 1).toUpperCase();
  const fileName = displayName.substring(0, displayName.lastIndexOf('.'));
  const fileSize = attachedFile?.file?.size;
  const url = document?.url || attachedFile?.url;

  const handleDownload = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="flex p-2 gap-1 rounded-lg border border-[#F0F0F0]" style={{ backgroundColor: '#F9F9F9' }}>
      <div className="flex gap-2">
        <div
          className="w-11 h-11 border border-[#D8D9D7] rounded flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: thumbnailUrl ? 'transparent' : '#fff' }}
        >
          {thumbnailUrl && !attachedFile?.uploading ? (
            <img
              src={thumbnailUrl}
              alt={displayName}
              draggable={false}
              className="w-full h-full object-cover pointer-events-none"
            />
          ) : (
            <>
              {attachedFile?.uploading && <CircularProgress size={20} />}
              {attachedFile?.error && <RiErrorWarningLine size={20} className="text-error" />}
              {!attachedFile?.uploading && !attachedFile?.error && !thumbnailUrl && (
                <RiFileTextLine size={20} className="text-success" />
              )}
            </>
          )}
        </div>
        <div>
          <AgText size="14" color="secondary" weight="medium">
            {fileName}
          </AgText>
          <div className="flex gap-2">
            <AgText size="14" color="secondary">
              {fileExtension}
            </AgText>
            {fileSize && (
              <>
                <AgText size="14" className="text-neutral-6">
                  •
                </AgText>
                <AgText size="14" color="secondary">
                  {formatFileSize(fileSize)}
                </AgText>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="h-full flex items-center">
        {mode === 'edit' && onRemove && (
          <AgButton variant="ghost" size="icon-xs" shape="circle" onPress={onRemove}>
            <RiCloseCircleFill size={16} />
          </AgButton>
        )}
        {mode === 'view' && url && (
          <AgButton variant="ghost" size="icon-xs" shape="circle" onPress={handleDownload}>
            <RiDownloadLine size={16} />
          </AgButton>
        )}
      </div>
    </div>
  );
}
